import type { QueryFilter as TQueryFilter } from '@schibsted-nmp/search-frontend-clients/quest';
import { IconSearch16 } from '@warp-ds/icons/react/search-16';
import { Affix, Combobox } from '@warp-ds/react';
import React from 'react';
import { useAutocompleteData } from '../../../data/AutocompleteData';
import { useSubmitSearch } from '../../../data/SearchQuery';
import { IconAffix } from './IconAffix';
import { useQueryValue } from './useQueryValue';

type QueryFilterProps = {
    className?: string;
    filter: TQueryFilter;
};

export function QueryFilter(props: QueryFilterProps) {
    const { className = '', filter } = props;

    const { queryValue, setQueryValue } = useQueryValue(filter);
    const { submitSearch } = useSubmitSearch();

    const filterName = filter.name;

    const handleSubmit = React.useCallback(
        (ev: React.FormEvent) => {
            // Prevent page reload on form submit
            ev.preventDefault();

            submitSearch((urlParams) => {
                if (queryValue) {
                    urlParams.set(filterName, queryValue);
                } else {
                    urlParams.delete(filterName);
                }
            });
        },
        [filterName, submitSearch, queryValue],
    );

    const { autocompleteData } = useAutocompleteData({
        rows: 6,
        term: queryValue,
    });

    const handleSelect = React.useCallback(
        (value: string) => {
            setQueryValue(value);
            submitSearch((urlParams) => urlParams.set(filterName, value));
        },
        [filterName, setQueryValue, submitSearch],
    );

    const handleClear = React.useCallback(() => {
        setQueryValue('');
        submitSearch((urlParams) => urlParams.delete(filterName));
    }, [filterName, setQueryValue, submitSearch]);

    const options = React.useMemo(
        () =>
            autocompleteData.map(({ suggest }) => ({
                value: suggest,
            })),
        [autocompleteData],
    );

    return (
        <form className={`${className} flex space-x-8`} onSubmit={handleSubmit}>
            <div className="grow">
                <Combobox
                    aria-label={filter.display_name}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    disableStaticFiltering
                    enterKeyHint="search"
                    matchTextSegments
                    name={filter.name}
                    onChange={setQueryValue}
                    // @ts-expect-error Type 'SyntheticEvent<HTMLInputElement, Event>' is not assignable to type 'string'.ts(2322)
                    onSelect={handleSelect}
                    options={options}
                    spellCheck="false"
                    value={queryValue}
                    type="search"
                >
                    <IconAffix prefix>
                        <IconSearch16 />
                    </IconAffix>
                    {queryValue && <Affix suffix clear onClick={handleClear} />}
                </Combobox>
            </div>
            {/* <Button className="hidden sm:inline-block" type="submit">
                {i18n.t({
                    id: 'filters.button.search',
                    message: 'Search',
                })}
            </Button> */}
        </form>
    );
}
